import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, User, Lock, Mail, Phone, Building2 } from 'lucide-react';

const Profile = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPasswordDisplay, setNewPasswordDisplay] = useState('');
  const [paidProjects, setPaidProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check password strength
  useEffect(() => {
    const pwd = passwordData.new_password;
    setPasswordStrength({
      length: pwd.length >= 12,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      special: /[!@#$%^&*]/.test(pwd)
    });
  }, [passwordData.new_password]);

  useEffect(() => {
    if (user && user.role === 'user') {
      fetchPaidProjectsWithBankDetails();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchPaidProjectsWithBankDetails = async () => {
    try {
      // Get user's approved interests
      const interestsRes = await axios.get(`${API}/interest`);
      const userInterests = interestsRes.data;

      // Get all projects
      const projectsRes = await axios.get(`${API}/projects`);
      const allProjects = projectsRes.data;

      // Filter for projects where user has approved interest
      const projectsWithBankDetails = [];
      
      userInterests.forEach(interest => {
        if (interest.status === 'approved') {
          const project = allProjects.find(p => p.id === interest.project_id);
          if (project && project.bank_details) {
            projectsWithBankDetails.push({
              project_id: project.id,
              project_title: project.title,
              project_image: project.image_url,
              bank_details: project.bank_details
            });
          }
        }
      });

      setPaidProjects(projectsWithBankDetails);
    } catch (error) {
      console.error('Error fetching bank details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Neue Passwörter stimmen nicht überein');
      return;
    }

    // Password strength will be validated by backend
    const allRequirementsMet = Object.values(passwordStrength).every(v => v === true);
    if (!allRequirementsMet) {
      toast.error('Bitte erfüllen Sie alle Passwort-Anforderungen');
      return;
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      
      // Store new password for display
      const newPwd = passwordData.new_password;
      
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      
      // Show password one-time display
      setNewPasswordDisplay(newPwd);
      setShowPasswordModal(true);
      
      toast.success('Passwort erfolgreich geändert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setChangingPassword(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/bess/dashboard')} className="mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Zurück zum Dashboard
          </Button>
          <h1 className="text-4xl font-bold text-slate-900">Mein Profil</h1>
          <p className="text-slate-600 mt-2">Verwalten Sie Ihre persönlichen Daten</p>
        </div>

        <div className="grid gap-6">
          {/* User Information Card */}
          <div className="card">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <User className="mr-3 text-green-600" size={24} />
              Persönliche Informationen
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center p-4 bg-slate-50 rounded-lg border border-slate-200">
                <User className="text-slate-600 mr-3" size={20} />
                <div>
                  <p className="text-sm text-slate-600">Benutzername</p>
                  <p className="font-semibold text-slate-900">{user.username}</p>
                </div>
              </div>

              <div className="flex items-center p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Mail className="text-slate-600 mr-3" size={20} />
                <div>
                  <p className="text-sm text-slate-600">E-Mail-Adresse</p>
                  <p className="font-semibold text-slate-900">{user.email}</p>
                </div>
              </div>

              {user.phone_number && (
                <div className="flex items-center p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <Phone className="text-slate-600 mr-3" size={20} />
                  <div>
                    <p className="text-sm text-slate-600">Telefonnummer</p>
                    <p className="font-semibold text-slate-900">{user.phone_number}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-blue-600 text-white mr-3">
                  <span className="text-xs font-bold">{user.role.toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Rolle</p>
                  <p className="font-semibold text-slate-900 capitalize">{user.role}</p>
                </div>
              </div>

              <div className="flex items-center p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className={`w-3 h-3 rounded-full mr-3 ${user.is_verified ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                <div>
                  <p className="text-sm text-slate-600">Verifizierungsstatus</p>
                  <p className="font-semibold text-slate-900">
                    {user.is_verified ? 'Verifiziert' : 'Nicht verifiziert'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Change Password Card */}
          <div className="card">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
              <Lock className="mr-3 text-blue-600" size={24} />
              Passwort ändern
            </h2>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="current_password">Aktuelles Passwort</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  placeholder="Ihr aktuelles Passwort"
                />
              </div>

              <div>
                <Label htmlFor="new_password">Neues Passwort</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  placeholder="Mindestens 12 Zeichen"
                />
                <div className="mt-2 p-3 bg-slate-50 rounded border border-slate-200">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Passwort-Anforderungen:</p>
                  <div className="space-y-1">
                    <div className={`text-xs flex items-center gap-2 ${passwordStrength.length ? 'text-green-600' : 'text-slate-500'}`}>
                      <span>{passwordStrength.length ? '✓' : '○'}</span>
                      <span>Mindestens 12 Zeichen</span>
                    </div>
                    <div className={`text-xs flex items-center gap-2 ${passwordStrength.uppercase ? 'text-green-600' : 'text-slate-500'}`}>
                      <span>{passwordStrength.uppercase ? '✓' : '○'}</span>
                      <span>Mindestens ein Großbuchstabe (A-Z)</span>
                    </div>
                    <div className={`text-xs flex items-center gap-2 ${passwordStrength.lowercase ? 'text-green-600' : 'text-slate-500'}`}>
                      <span>{passwordStrength.lowercase ? '✓' : '○'}</span>
                      <span>Mindestens ein Kleinbuchstabe (a-z)</span>
                    </div>
                    <div className={`text-xs flex items-center gap-2 ${passwordStrength.number ? 'text-green-600' : 'text-slate-500'}`}>
                      <span>{passwordStrength.number ? '✓' : '○'}</span>
                      <span>Mindestens eine Zahl (0-9)</span>
                    </div>
                    <div className={`text-xs flex items-center gap-2 ${passwordStrength.special ? 'text-green-600' : 'text-slate-500'}`}>
                      <span>{passwordStrength.special ? '✓' : '○'}</span>
                      <span>Mindestens ein Sonderzeichen (!@#$%^&*)</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm_password">Neues Passwort bestätigen</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  placeholder="Passwort wiederholen"
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                >
                  {changingPassword ? 'Wird geändert...' : 'Passwort ändern'}
                </Button>
              </div>
            </form>
          </div>

          {/* Bank Details Card - Only for users with approved interests */}
          {user.role === 'user' && paidProjects.length > 0 && (
            <div className="card">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center">
                <Building2 className="mr-3 text-green-600" size={24} />
                Bankverbindungen Ihrer Projekte
              </h2>
              <p className="text-sm text-slate-600 mb-6">
                Hier finden Sie die Bankverbindungen der Projekte, für die Ihr Interesse genehmigt wurde.
              </p>

              <div className="space-y-6">
                {paidProjects.map((item) => (
                  <div key={item.project_id} className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* Project Header */}
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 border-b border-slate-200">
                      <div className="flex items-center gap-3">
                        {item.project_image && (
                          <img 
                            src={item.project_image} 
                            alt={item.project_title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        <h3 className="text-lg font-bold text-slate-900">{item.project_title}</h3>
                      </div>
                    </div>

                    {/* Bank Details */}
                    <div className="p-4 bg-white">
                      {item.bank_details && (item.bank_details.iban || item.bank_details.account_holder) ? (
                        <div className="space-y-3">
                          {item.bank_details.account_holder && (
                            <div className="flex flex-col">
                              <span className="text-xs text-slate-500 mb-1">Empfänger</span>
                              <span className="font-medium text-slate-900">{item.bank_details.account_holder}</span>
                            </div>
                          )}
                          
                          {item.bank_details.iban && (
                            <div className="flex flex-col">
                              <span className="text-xs text-slate-500 mb-1">IBAN</span>
                              <span className="font-mono font-medium text-slate-900 text-sm">{item.bank_details.iban}</span>
                            </div>
                          )}
                          
                          {item.bank_details.bic && (
                            <div className="flex flex-col">
                              <span className="text-xs text-slate-500 mb-1">BIC</span>
                              <span className="font-mono font-medium text-slate-900">{item.bank_details.bic}</span>
                            </div>
                          )}
                          
                          {item.bank_details.bank_name && (
                            <div className="flex flex-col">
                              <span className="text-xs text-slate-500 mb-1">Bank</span>
                              <span className="font-medium text-slate-900">{item.bank_details.bank_name}</span>
                            </div>
                          )}
                          
                          {item.bank_details.purpose && (
                            <div className="flex flex-col pt-2 border-t border-slate-200">
                              <span className="text-xs text-slate-500 mb-1">Verwendungszweck</span>
                              <div className="bg-blue-50 p-2 rounded border border-blue-200">
                                <span className="font-medium text-blue-900 text-sm">{item.bank_details.purpose}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 italic">Keine Bankverbindung hinterlegt</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* One-Time Password Display Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-green-600 mb-2">Passwort erfolgreich geändert!</h3>
            </div>
            
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-4">
              <p className="text-sm font-semibold text-yellow-900 mb-2">
                ⚠️ WICHTIG: Einmalige Anzeige
              </p>
              <p className="text-xs text-yellow-800 mb-3">
                Bitte kopieren Sie Ihr neues Passwort jetzt. Es wird nur einmal angezeigt.
              </p>
              
              <div className="bg-white rounded border-2 border-yellow-600 p-3 mb-3">
                <p className="text-xs text-slate-600 mb-1">Ihr neues Passwort:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-lg font-mono font-bold text-slate-900 break-all">
                    {newPasswordDisplay}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(newPasswordDisplay);
                      toast.success('Passwort kopiert!');
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold"
                  >
                    📋 Kopieren
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-xs text-blue-900">
                💡 <strong>Tipp:</strong> Speichern Sie Ihr Passwort an einem sicheren Ort, z.B. in einem Passwort-Manager.
              </p>
            </div>

            <button
              onClick={() => {
                setShowPasswordModal(false);
                setNewPasswordDisplay('');
              }}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
            >
              ✓ Verstanden, Passwort gespeichert
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
